import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

export const ACTION_MENU_POPOVER_ATTR = "data-action-menu-popover";

const GAP = 8;

function computePosition(anchorEl, menuEl, align) {
  const rect = anchorEl.getBoundingClientRect();
  const menuWidth = menuEl?.offsetWidth ?? 144;
  const menuHeight = menuEl?.offsetHeight ?? 160;

  let left = align === "end" ? rect.right - menuWidth : rect.left;
  if (left < GAP) left = GAP;
  if (left + menuWidth > window.innerWidth - GAP) {
    left = Math.max(GAP, window.innerWidth - menuWidth - GAP);
  }

  let top = rect.bottom + GAP;
  if (top + menuHeight > window.innerHeight - GAP) {
    const above = rect.top - GAP - menuHeight;
    if (above >= GAP) {
      top = above;
    }
  }

  return { top, left };
}

export default function ActionMenuPopover({
  anchorRef,
  isOpen,
  onClose,
  align = "end",
  className = "min-w-[8rem] w-32",
  children,
}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const reposition = useCallback(() => {
    const anchor = anchorRef?.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;
    setPos(computePosition(anchor, menu, align));
  }, [anchorRef, align]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    reposition();
    const r1 = requestAnimationFrame(reposition);
    const r2 = requestAnimationFrame(reposition);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, reposition, children]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={menuRef}
      {...{ [ACTION_MENU_POPOVER_ATTR]: "true" }}
      className={`fixed z-[200] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl ${className}`}
      style={{ top: pos.top, left: pos.left }}
      role="menu"
    >
      {children}
    </div>,
    document.body
  );
}

ActionMenuPopover.propTypes = {
  anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  align: PropTypes.oneOf(["start", "end"]),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};
